import React from 'react'
// libs
import { change } from 'redux-form'
import { useDispatch } from 'react-redux'
// hooks
import useModalData, { useShowModal } from 'hooks/useShowModal'
// views
import { MatrixContainer, MatrixBottomSection } from './views'
// components
import TopMatrixSection from './components/TopMatrixSection'
import LeftMatrixSection from './components/LeftMatrixSection'
import RightMatrixSection from './components/RightMatrixSection'
import MatrixOptionsModal from 'components/Modals/MatrixOptionsModal'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
import AppetiteOptionsModal from 'components/Modals/AppetiteOptionsModal'
import AppetiteConfirmationModal from 'components/Modals/AppetiteConfirmationModal'
// constants
import { Modals, Forms } from 'constants/index'
// types
import { MatrixOption, TMatrix } from './types'
// utils
import { v4 as uuid } from 'uuid'
import { get, capitalize } from 'lodash'
import { findMaxAppetite } from './utils'

// types
interface Props {
  label: string
  value: TMatrix | undefined
  disabled: boolean
  onChange: (matrix: TMatrix) => void
  fieldName: string
  hideModal: () => void
  appetiteEnabled: boolean
}

type RowCellValueChangeHandlerFn = (key: string) => RowCellValueChangeFn

export type RowCellValueChangeFn = (idx: number, id: string) => (value: string) => void

const Matrix: React.FC<Props> = ({ label, fieldName, hideModal, value, disabled, onChange, appetiteEnabled }) => {
  const dispatch = useDispatch()

  const [matrix, setMatrix] = React.useState(value)

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_MATRIX_ITEM_MODAL })
  const { showModal: showAppetiteConf } = useShowModal({ modalType: Modals.RISK_MATRIX_APPETITE_CONF_MODAL })

  const {
    modalData: { entity }
  } = useModalData({ modalType: Modals.CONFIRM_DELETE_MATRIX_ITEM_MODAL })

  const onMatrixOptionsChange = (data: MatrixOption[]): void => {
    setMatrix((prev) => ({
      ...prev,
      options: data,
      bRightSection: prev.bRightSection.map((row) =>
        row.map((subRow) => {
          const value = data.find(({ id }) => id === get(subRow.value, 'id'))

          if (value) {
            return { ...subRow, value }
          }
          return subRow
        })
      )
    }))
  }

  const onMatrixOptionsSelect = async (optionId: string) => {
    if (appetiteEnabled) {
      showAppetiteConf({
        optionId,
        onConfirm: () => {
          dispatch(
            change(Forms.RISK_TEMPLATE_FORM, fieldName, {
              value: optionId,
              selectedTs: matrix.topSection
                .flatMap((row) => row.filter((item) => item.selected))
                .map(({ id }) => ({ id }))
            })
          )
          hideModal()
        },
        onCancel: () => {
          setMatrix((prev) => ({ ...prev, selectedOption: prev.selectedOption }))
        }
      })
    } else {
      dispatch(
        change(Forms.RISK_TEMPLATE_FORM, fieldName, {
          value: optionId
        })
      )
      hideModal()
    }
  }

  const onRowCellValueChange: RowCellValueChangeHandlerFn = (key) => (idx, id) => (value) => {
    setMatrix((prev) => ({
      ...prev,
      [key]: prev[key].map((row, rowIdx) => {
        if (rowIdx === idx) {
          return row.map((subRow) => {
            if (subRow.id === id) {
              return { ...subRow, value }
            }
            return subRow
          })
        }
        return row
      })
    }))
  }

  const removeRow = (dataKey: string, relatedDataKey?: string, entity?: string) => () => {
    if (matrix[dataKey].length === 1) {
      return
    }

    showModal({
      entity,
      width: 'sm',
      handleConfirm: () =>
        setMatrix((prev) => {
          const newMatrix = {
            ...prev,
            [dataKey]: prev[dataKey].length > 1 ? prev[dataKey].slice(0, -1) : prev[dataKey],
            appetites:
              dataKey === 'topSection' && prev.appetites?.length > 0 ? prev.appetites.slice(0, -1) : prev.appetites
          }

          if (relatedDataKey) {
            newMatrix[relatedDataKey] = prev[relatedDataKey].map((row) => (row.length > 2 ? row.slice(0, -1) : row))
          }

          return newMatrix
        })
    })
  }

  const onAppetitesOptionsSave = (options: any[], idx: number) => {
    setMatrix((prev) => ({
      ...prev,
      appetites: prev.appetites.map((item, index) => {
        if (index === idx) {
          const value = findMaxAppetite(options, matrix.options)

          return { ...item, options, value }
        }

        return item
      })
    }))
  }

  React.useEffect(() => {
    onChange(matrix)
  }, [matrix])

  React.useEffect(() => {
    if (appetiteEnabled && !matrix.appetites?.length) {
      setMatrix((prev) => ({
        ...prev,
        appetites: new Array(matrix.topSection.length).fill({ id: uuid(), options: [] })
      }))
    }
  }, [matrix, appetiteEnabled])

  return (
    <MatrixContainer>
      <TopMatrixSection
        matrix={matrix}
        disabled={disabled}
        setData={setMatrix}
        appetiteEnabled={appetiteEnabled}
        removeRow={removeRow('topSection')}
        rightRowsLength={matrix.bRightSection.length}
        onCellValueChange={onRowCellValueChange('topSection')}
      />

      <MatrixBottomSection>
        <LeftMatrixSection
          disabled={disabled}
          setData={setMatrix}
          data={matrix.bLeftSection}
          relatedDataKey='bRightSection'
          onCellValueChange={onRowCellValueChange('bLeftSection')}
          removeRow={removeRow('bLeftSection', 'bRightSection')}
        />
        <RightMatrixSection
          matrix={matrix}
          disabled={disabled}
          setData={setMatrix}
          relatedDataKey='topSection'
          appetiteEnabled={appetiteEnabled}
          onOptionsSelect={onMatrixOptionsSelect}
          leftRowsLength={matrix.bLeftSection.length}
          onCellValueChange={onRowCellValueChange('bRightSection')}
          removeRow={removeRow('bRightSection', 'topSection', 'column')}
        />
      </MatrixBottomSection>

      <AppetiteConfirmationModal label={label} matrix={matrix} />
      <MatrixOptionsModal initialOptions={matrix.options} onSave={onMatrixOptionsChange} />
      <AppetiteOptionsModal
        matrixOptions={matrix.options}
        onSave={onAppetitesOptionsSave}
        initialOptions={matrix.appetites}
      />
      <ConfirmDeleteModal
        header={`Delete ${capitalize(entity) || 'Row'}`}
        modalType={Modals.CONFIRM_DELETE_MATRIX_ITEM_MODAL}>
        Are you sure you want to delete this {entity || 'row'}?
      </ConfirmDeleteModal>
    </MatrixContainer>
  )
}

export default Matrix
